/**
 * A group's scope (D14): the whole tenant, or chosen sites and studios —
 * pickers over the tenant's on-prem sites and its studios. A resources scope
 * with nothing chosen grants nothing, which the editor says.
 */
import type { GroupWire } from '@acp/api-contract/tenancy.js';

export type ScopeChoice = { readonly id: string; readonly name: string };

export type ScopeEditorProps = {
  readonly scope: GroupWire['scope'];
  readonly sites: readonly ScopeChoice[];
  readonly studios: readonly ScopeChoice[];
  /** The Admin group must stay tenant-wide. */
  readonly tenantOnly: boolean;
  readonly disabled: boolean;
  readonly onChange: (scope: GroupWire['scope']) => void;
};

function Picker({ legend, choices, selected, disabled, onChange }: { readonly legend: string; readonly choices: readonly ScopeChoice[]; readonly selected: readonly string[]; readonly disabled: boolean; readonly onChange: (ids: string[]) => void }) {
  return (
    <fieldset className="grid gap-1" data-testid={`scope-${legend.toLowerCase()}`}>
      <legend className="mb-1 text-xs font-medium uppercase text-muted-foreground">{legend}</legend>
      {choices.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : null}
      {choices.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={selected.includes(c.id)} disabled={disabled} onChange={(e) => onChange(e.target.checked ? [...selected, c.id] : selected.filter((id) => id !== c.id))} />
          {c.name}
          <span className="font-mono text-xs text-muted-foreground">{c.id}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function ScopeEditor({ scope, sites, studios, tenantOnly, disabled, onChange }: ScopeEditorProps) {
  const resources = scope.kind === 'resources' ? scope : { kind: 'resources' as const, siteIds: [], studioIds: [] };
  return (
    <fieldset className="grid gap-3" data-testid="scope-editor">
      <legend className="mb-2 text-sm font-medium">Scope</legend>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" name="scope-kind" checked={scope.kind === 'tenant'} disabled={disabled} onChange={() => onChange({ kind: 'tenant' })} data-testid="scope-tenant" />
        The whole tenant
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" name="scope-kind" checked={scope.kind === 'resources'} disabled={disabled || tenantOnly} onChange={() => onChange(resources)} data-testid="scope-resources" />
        Only these sites and studios
        {tenantOnly ? <span className="text-xs text-muted-foreground">(this group stays tenant-wide)</span> : null}
      </label>
      {scope.kind === 'resources' ? (
        <div className="grid gap-4 pl-6 sm:grid-cols-2">
          <Picker legend="Sites" choices={sites} selected={scope.siteIds} disabled={disabled} onChange={(siteIds) => onChange({ ...scope, siteIds })} />
          <Picker legend="Studios" choices={studios} selected={scope.studioIds} disabled={disabled} onChange={(studioIds) => onChange({ ...scope, studioIds })} />
          {scope.siteIds.length === 0 && scope.studioIds.length === 0 ? (
            <p role="status" className="rounded-md bg-warning px-2 py-1 text-sm text-warning-foreground sm:col-span-2" data-testid="scope-empty">
              Nothing chosen: this group grants nothing until a site or studio is.
            </p>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
